import React, { useState, useMemo } from "react";
import CopyButton from "@/components/CopyButton";
import { Calculator, TrendingDown } from "lucide-react";

const OFFER_DOC = `OFFER NAME: The 60-Second Consult Booking System

MARKET: Med spas and aesthetic clinics

THE OUTCOME: Never lose another ad lead to a slow reply. Every inquiry
answered in under 60 seconds, day or night, and qualified consults land
directly on your books.

WHAT IT REPLACES: A second front-desk coordinator ($36,000 to $42,000/yr)
and it works the 128 hours a week your desk is closed.

THE LEAK IT PLUGS: A spa spending $3K/mo on ads generates ~80 leads.
Industry reality: leads contacted within 5 minutes convert at multiples of
leads contacted in hours. A desk that replies "when they can" loses 25 to
40% of paid leads. At $1,800 avg first-package value, that's $36,000 to
$57,000 a year in paid-for leads that never book.

HOW IT WORKS (customer-facing):
1. A lead texts, chats, or fills a form; the system responds in under 60s
2. It answers treatment questions and screens with your consult criteria
3. Qualified prospects book straight into your consult calendar; your team
   gets a summary of every conversation

BUILD SPEC (freelancer-facing):
- Channels: SMS + website chat widget + Instagram DM
- Connects to: their booking calendar (most spas: a mainstream booking
  platform) and a simple pipeline board
- Qualification logic: treatment interest, timeline, budget banding,
  prior treatments, contraindication screen (flag, never advise), zip code
- Handoff rule: any pricing negotiation, medical question, or upset tone
  routes instantly to staff with full transcript
- Compliance notes: no medical advice, no diagnosis, consent line on first
  message, opt-out honored instantly

PRICE: $4,500 setup + $750/mo
GUARANTEE: 15 additional booked consults in the first 30 days, or we
manage it free until you get them.
ONBOARDING CAPACITY: 4 new clients/mo.`;

const OPENERS = [
  { v: "Variant A (the mystery-shop)", text: `"Hey [Name], quick heads-up: I inquired about [treatment] through your site [day] at [time] and got a reply [X hours] later. In this market that lead usually books wherever answers first. I build systems for spas that answer every inquiry in under 60 seconds, nights and weekends included. Worth a 15-minute look at what you're leaking?"` },
  { v: "Variant B (the math hook)", text: `"[Name], if you're running ads for [treatment], rough math says a reply time over an hour is costing you 1 in 3 paid leads. I fix exactly that for med spas: every lead answered in under 60 seconds and qualified consults booked straight to your calendar. Open to seeing the numbers on your volume?"` },
  { v: "Variant C (the referral-style)", text: `"[Name], I work with aesthetic clinics on lead response. Owners keep telling me the same thing: front desk is slammed, after-hours leads go cold, and ad costs keep rising. Built a system that answers every inquiry in 60 seconds and books consults automatically. Happy to show you it live on your own website. Interested?"` },
];

const FAQ = [
  ["\"Will it sound robotic to my patients?\"", "Demo it live on the call. Let them text it. The experience closes better than any claim."],
  ["\"My front desk handles this.\"", "\"During business hours, when they're not with a patient, absolutely. This covers the other 128 hours a week and the 40 inquiries that arrive while they're checking someone in.\""],
  ["\"We tried a chatbot before.\"", "\"Most spas have. A chatbot answers FAQs. This qualifies against YOUR consult criteria and books YOUR calendar. That's the difference between a toy and a coordinator.\""],
  ["\"$4,500 is a lot.\"", "Return to the Value Math Sheet. Their numbers, their leak, their ratio."],
];

const CHECKLIST = [
  "Onboarding call: collect calendar access, consult criteria, treatment FAQ doc, brand voice notes (45 min)",
  "Freelancer configures build against this spec (5 to 10 days)",
  "Internal test week: you and the owner text the system, tune responses",
  "Go live; monitor daily for week one, weekly after",
  "Day-30 report: consults booked, conversations handled, response times. This report is your renewal AND your referral ask.",
];

const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtLeads = (x) => (Number.isInteger(x) ? String(x) : x.toFixed(1));
const YEAR_ONE_COST = 4500 + 12 * 750; // = 13,500

// Hoisted to module scope so they are NOT redefined on each keystroke. Defining them
// inside the parent recreates the component type every render, which remounts the
// <input> and steals focus. Plain text + inputMode numeric for phones/tablets.
function InputRow({ label, prefix, suffix, value, set, testid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid var(--line)" }}>
      <span style={{ fontSize: 15 }}>{label}</span>
      <span style={{ fontFamily: "IBM Plex Mono, monospace", display: "inline-flex", alignItems: "center", gap: 2 }}>
        {prefix}<input type="text" inputMode="numeric" value={value} onChange={(e) => set(e.target.value.replace(/[^0-9.]/g, ""))} data-testid={testid}
          style={{ width: 90, textAlign: "right", border: "1px solid var(--line)", borderRadius: 8, padding: "6px 8px", fontFamily: "IBM Plex Mono, monospace", fontSize: 15 }} />{suffix}
      </span>
    </div>
  );
}
function OutRow({ label, value, testid, strong, highlight }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid var(--line)", background: highlight ? "#FFFBEA" : "transparent" }}>
      <span style={{ fontSize: 15, fontWeight: strong ? 700 : 400 }}>{label}</span>
      <span className="font-mono-plex" data-testid={testid} style={{ fontWeight: strong ? 700 : 500, color: highlight ? "#8A6D00" : "var(--ink)", fontSize: strong ? 17 : 15 }}>{value}</span>
    </div>
  );
}

function ValueMath() {
  // String state so the field can be temporarily empty while typing (no snap-to-0).
  const [adSpend, setAdSpend] = useState("3000");
  const [leads, setLeads] = useState("80");
  const [reachedPct, setReachedPct] = useState("20");
  const [avgValue, setAvgValue] = useState("1800");
  const [lossPct, setLossPct] = useState("33");

  const { leadsLost, monthlyLeak, annualLeak, ratio } = useMemo(() => {
    // Compute from the UNROUNDED leads-lost value so we never inflate the leak.
    const leadsLost = (Number(leads) || 0) * (Number(lossPct) || 0) / 100;
    const monthlyLeak = leadsLost * (Number(avgValue) || 0);
    const annualLeak = monthlyLeak * 12;
    const ratio = YEAR_ONE_COST > 0 ? annualLeak / YEAR_ONE_COST : 0;
    return { leadsLost, monthlyLeak, annualLeak, ratio };
  }, [leads, lossPct, avgValue]);

  return (
    <div className="card-paper" data-testid="value-math-calculator" style={{ padding: 26, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Calculator size={20} color="#2038E6" />
        <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 900, textTransform: "uppercase", fontSize: 18, margin: 0 }}>Value Math Sheet</h3>
      </div>
      <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6 }}>Walk the prospect through with THEIR numbers.</p>

      <div style={{ marginTop: 12 }}>
        <InputRow label="Monthly ad spend" prefix="$" value={adSpend} set={setAdSpend} testid="calc-ad-spend" />
        <InputRow label="Leads per month" value={leads} set={setLeads} testid="calc-leads" />
        <InputRow label="Leads currently reached within 5 minutes" value={reachedPct} set={setReachedPct} suffix="%" testid="calc-reached" />
        <InputRow label="Average first-package value" prefix="$" value={avgValue} set={setAvgValue} testid="calc-value" />
        <InputRow label="Leads lost to slow response (est. 25 to 40%)" value={lossPct} set={setLossPct} suffix="%" testid="calc-loss-pct" />
        <OutRow label="Leads lost per month" value={`${fmtLeads(leadsLost)} leads`} testid="calc-leads-lost" />
        <OutRow label="Monthly revenue leak" value={fmt(monthlyLeak)} testid="calc-monthly-leak" strong />
        <OutRow label="Annual leak" value={fmt(annualLeak)} testid="calc-annual-leak" strong />
        <OutRow label="System cost, year one ($4,500 + 12 × $750)" value={fmt(YEAR_ONE_COST)} testid="calc-year-one-cost" />
        <OutRow label="Leak-to-cost ratio" value={`${ratio.toFixed(1)} : 1`} testid="calc-ratio" strong highlight />
      </div>

      <p style={{ fontSize: 14, color: "var(--muted)", marginTop: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
        <TrendingDown size={16} style={{ flexShrink: 0, marginTop: 3 }} />
        If the ratio isn't at least 2:1 with conservative inputs, disqualify them honestly. Protecting your guarantee protects your business.
      </p>
    </div>
  );
}

export default function DemoOffer() {
  return (
    <div data-testid="demo-offer-page">
      <div className="eyebrow" style={{ color: "#2038E6" }}>Demo Offer</div>
      <h1 className="h-display" style={{ fontSize: "clamp(32px,5vw,48px)", marginTop: 8 }}>The Working Demo Offer</h1>
      <p style={{ color: "var(--muted)", marginTop: 10, fontSize: 17 }}>A Complete, Launch-Ready Offer You Can Model or Deploy As-Is</p>
      <p style={{ fontSize: 15, marginTop: 8 }}>This is the proof asset. Everything below is a finished offer for one market (med spas). Customers can launch this exact offer, swap the market, or use it as the reference standard for building their own.</p>

      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 32 }}>The Offer At A Glance</h2>
      <div className="card-paper prose-weekend" style={{ padding: 22, marginTop: 12 }}>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li style={{ margin: "6px 0" }}><strong>Name:</strong> The 60-Second Consult Booking System</li>
          <li style={{ margin: "6px 0" }}><strong>Market:</strong> Med spas and aesthetic clinics (2 to 15 providers)</li>
          <li style={{ margin: "6px 0" }}><strong>Outcome:</strong> Every lead answered in under 60 seconds, qualified, and booked to a consult, 24 hours a day.</li>
          <li style={{ margin: "6px 0" }}><strong>Price:</strong> $4,500 setup + $750/mo management</li>
          <li style={{ margin: "6px 0" }}><strong>Running cost to you:</strong> roughly $100 to $180/mo in platform subscriptions per client</li>
          <li style={{ margin: "6px 0" }}><strong>Margin:</strong> setup is nearly all margin after the one-time build; monthly runs 75%+ margin</li>
        </ul>
      </div>

      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 32 }}>The One-Page Offer Doc (Completed Example)</h2>
      <pre style={{ marginTop: 12, padding: 18, border: "1px solid var(--line)", borderRadius: 12, background: "#fff", fontFamily: "IBM Plex Mono, monospace", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }} data-testid="demo-offer-doc">{OFFER_DOC}</pre>

      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 32 }}>The Value Math Sheet</h2>
      <ValueMath />

      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 32 }}>Outreach Openers For This Offer (3 Variants)</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
        {OPENERS.map((o, i) => (
          <div key={i} className="card-paper" data-testid={`opener-${i + 1}`} style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid var(--line)", background: "#f2f3f0" }}>
              <span className="eyebrow">{o.v}</span>
              <CopyButton text={o.text} testId={`opener-copy-${i + 1}`} />
            </div>
            <div style={{ padding: 16, fontSize: 15 }}>{o.text}</div>
          </div>
        ))}
      </div>

      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 32 }}>FAQ Objection Keys For This Offer</h2>
      <div className="prose-weekend" style={{ marginTop: 12 }}>
        {FAQ.map(([q, a], i) => (
          <div key={i} style={{ padding: "12px 0", borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
            <div style={{ fontWeight: 700 }}>{q}</div>
            <div style={{ color: "var(--muted)", marginTop: 4 }}>→ {a}</div>
          </div>
        ))}
      </div>

      <h2 className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 22, marginTop: 32 }}>Delivery Checklist (Post-Close)</h2>
      <ol className="prose-weekend" style={{ marginTop: 12, paddingLeft: 20 }}>
        {CHECKLIST.map((c, i) => <li key={i} style={{ margin: "8px 0" }}>{c}</li>)}
      </ol>
    </div>
  );
}
